/* Klubregister: gemeinsamer Zustand (Modulbereich, wie die anderen geteilten Composables) und alle Aktionen.
 *
 * Das Register wird EINMAL pro Sitzung geladen (ensureLoaded, ausgelöst in App.vue nach der Anmeldung) und
 * dann aus dem Speicher benutzt. Schlägt das Laden fehl, bleibt die Liste leer und `error` gesetzt; alle
 * Anzeigen fallen dann auf den alten Text zurück (domain/footballClub.js resolveClub) — die App läuft weiter.
 *
 * Schreiben darf nur admin (die Datenbank erzwingt es; die Oberfläche zeigt den Dialog nur ihm). Alle
 * Aktionen werfen bei Fehler Error(<schlüssel>) — der Aufrufer zeigt errors.<schlüssel> über AppSnackbar,
 * nie eine Meldung der Datenbank (Details stehen in der Konsole, supabase/footballClubs.js).
 *
 * Nicht verwechseln mit domain/clubs.js (Club-Wechsler der App).
 */

import { ref, computed } from 'vue';
import * as api from '../supabase/footballClubs.js';
import { uploadFile, copyFile } from '../supabase/storage.js';
import { shrinkLogo } from './shrinkLogo.js';
import {
  clubsById, sortClubs, validateClubName, cleanClubName, canDeleteClub,
} from '../domain/footballClub.js';
import { uploadRegisterLogo } from '../domain/registerLogo.js';
import { copyLogosToRegister } from '../domain/clubLogoCopy.js';

const clubs = ref([]);
const loaded = ref(false);
const loading = ref(false);
const error = ref(null);
/** Map id -> { players, transfers }; nur für admin geladen (loadUsage) */
const usage = ref(new Map());
const usageLoaded = ref(false);
/** Klubregister-Dialog (Kontomenü, nur admin) offen? Geteilt, damit ClubSelect ihn öffnen kann. */
const registerDialogOpen = ref(false);

let inflight = null;
let lastFailedAt = 0;
/* Nach einem Fehler nicht bei jeder Komponente erneut fragen */
const RETRY_AFTER_MS = 30 * 1000;

/**
 * Alle Logo-Pfade des Registers, frisch aus der Datenbank — Schutz beim Aufräumen nach dem Speichern
 * (PlayerPanel.onSave -> fileCleanup `keep`). Wirft bei Fehler; der Aufrufer löscht dann NICHTS.
 */
export function fetchRegisterLogoPaths() {
  return api.allRegisterLogoPaths();
}

const uploadToStorage = async (file, folder) => ({ path: await uploadFile(file, folder), filename: file.name });

function replaceInList(club) {
  const i = clubs.value.findIndex((c) => c.id === club.id);
  if (i >= 0) clubs.value[i] = club;
  else clubs.value.push(club);
}

export function useFootballClubs() {
  const byId = computed(() => clubsById(clubs.value));
  const sorted = computed(() => sortClubs(clubs.value));
  const lackingLogo = computed(() => clubs.value.filter((c) => !c.logoPath).length);

  /** Register laden, höchstens einmal pro Sitzung (force: neu laden). Wirft nie: Fehler stehen in `error`. */
  async function ensureLoaded({ force = false } = {}) {
    if (loaded.value && !force) return;
    if (inflight) return inflight;
    if (!force && lastFailedAt && Date.now() - lastFailedAt < RETRY_AFTER_MS) return;
    loading.value = true;
    inflight = (async () => {
      try {
        clubs.value = await api.listClubs();
        loaded.value = true;
        error.value = null;
        lastFailedAt = 0;
      } catch (e) {
        error.value = e.message || 'loadFailed';
        lastFailedAt = Date.now();
      } finally {
        loading.value = false;
        inflight = null;
      }
    })();
    return inflight;
  }

  /** Nach dem Abmelden alles verwerfen: Der nächste Nutzer lädt neu. */
  function reset() {
    clubs.value = [];
    loaded.value = false;
    error.value = null;
    usage.value = new Map();
    usageLoaded.value = false;
    registerDialogOpen.value = false;
    inflight = null;
    lastFailedAt = 0;
  }

  /** Benutzungszahlen (nur admin) */
  async function loadUsage() {
    usage.value = await api.clubUsage();
    usageLoaded.value = true;
  }

  /** Verein anlegen: Name prüfen, Logo prüfen/verkleinern/hochladen, dann Zeile. Das Logo ist beim Anlegen Pflicht. */
  async function addClub({ name, file }) {
    const cleaned = cleanClubName(name);
    const check = validateClubName(cleaned, clubs.value);
    if (!check.ok) throw new Error(check.errors[0] === 'nameExists' ? 'clubExists' : 'invalidInput');
    if (!file) throw new Error('logoRequired');
    const id = crypto.randomUUID();
    /* Erst das Logo (wirft bei unzulässiger Datei), dann die Zeile mit der festen id: Scheitert das Anlegen,
       bleibt höchstens eine unbenutzte Datei liegen — es wird nie etwas gelöscht. */
    const stored = await uploadRegisterLogo(id, file, uploadToStorage, shrinkLogo);
    const club = await api.createClub({ id, name: cleaned, logoPath: stored.path });
    replaceInList(club);
    usage.value.set(club.id, { players: 0, transfers: 0 });
    return club;
  }

  async function renameClub(id, name) {
    const cleaned = cleanClubName(name);
    const check = validateClubName(cleaned, clubs.value, id);
    if (!check.ok) throw new Error(check.errors[0] === 'nameExists' ? 'clubExists' : 'invalidInput');
    const club = await api.updateClub(id, { name: cleaned });
    replaceInList(club);
    return club;
  }

  /** Logo tauschen: neue Datei hochladen, Pfad setzen. Die alte Datei bleibt liegen. */
  async function changeLogo(id, file) {
    const stored = await uploadRegisterLogo(id, file, uploadToStorage, shrinkLogo);
    const club = await api.updateClub(id, { logoPath: stored.path });
    replaceInList(club);
    return club;
  }

  /** Löschen nur ohne Benutzung; fehlen die Zahlen, wird NICHT gelöscht. Die Logo-Datei bleibt liegen. */
  async function removeClub(id) {
    if (!canDeleteClub(usage.value.get(id))) throw new Error('clubInUse');
    await api.deleteClub(id);
    clubs.value = clubs.value.filter((c) => c.id !== id);
    usage.value.delete(id);
  }

  /** Zusammenführen: danach Liste und Zahlen frisch laden. Liefert den Logo-Pfad des gelöschten Vereins (Datei bleibt liegen). */
  async function mergeInto(sourceId, targetId) {
    const logo = await api.mergeClubs(sourceId, targetId);
    await ensureLoaded({ force: true });
    await loadUsage();
    return logo;
  }

  /** "Logos ins Register kopieren" (Weg B): Kopie, nie Verschieben, wiederholbar. */
  async function copyLogos({ onProgress, shouldStop } = {}) {
    const result = await copyLogosToRegister({
      clubs: clubs.value,
      io: {
        copy: copyFile,
        setLogo: async (id, path) => replaceInList(await api.updateClub(id, { logoPath: path })),
      },
      onProgress,
      shouldStop,
    });
    return result;
  }

  return {
    clubs, sorted, byId, loaded, loading, error, usage, usageLoaded, lackingLogo, registerDialogOpen,
    ensureLoaded, reset, loadUsage, addClub, renameClub, changeLogo, removeClub, mergeInto, copyLogos,
  };
}
